export type MobileSortState = { key: string; direction: "asc" | "desc" } | null;
const labelCollator = new Intl.Collator("tr", { numeric: true });

export function nextMobileSort(current: MobileSortState, key: string): Exclude<MobileSortState, null> {
  return {
    key,
    direction: current?.key === key && current.direction === "desc" ? "asc" : "desc",
  };
}

function compareValues(left: string | number | null, right: string | number | null, direction: "asc" | "desc") {
  if (left == null) return right == null ? 0 : 1;
  if (right == null) return -1;
  const compared = typeof left === "number" && typeof right === "number"
    ? left - right
    : labelCollator.compare(String(left), String(right));
  return compared * (direction === "asc" ? 1 : -1);
}

export function sortMobileRows<T extends Record<string, unknown>>(
  rows: T[],
  sort: MobileSortState,
  valueFor: (row: T, key: string) => string | number | null,
  preserveGroups = false,
) {
  if (!sort) return rows;
  const sorted = (items: T[]) => [...items].sort((left, right) => compareValues(valueFor(left, sort.key), valueFor(right, sort.key), sort.direction));
  if (!preserveGroups) return sorted(rows);

  const groupOrder = Array.from(new Set(rows.map((row) => String(row.__group ?? "")).filter(Boolean)));
  if (!groupOrder.length) return sorted(rows);
  const grouped = new Map(groupOrder.map((group) => [group, { details: [] as T[], totals: [] as T[] }]));
  const ungrouped: T[] = [];
  for (const row of rows) {
    const bucket = grouped.get(String(row.__group ?? ""));
    if (!bucket) ungrouped.push(row);
    else if (row.__kind === "subtotal") bucket.totals.push(row);
    else bucket.details.push(row);
  }
  return [
    ...groupOrder.flatMap((group) => {
      const bucket = grouped.get(group);
      return bucket ? [...sorted(bucket.details), ...bucket.totals] : [];
    }),
    ...sorted(ungrouped),
  ];
}
